import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { HelpCircle, Home, DollarSign, Building2, Users, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import SEO from '@/components/SEO';

// FAQPage JSON-LD structured data for Google Rich Results
const FAQ_SCHEMA_ITEMS = [
  { q: "How much do I need to buy a home in Mississippi, Tennessee, or Arkansas?", a: "The amount needed to buy a home varies based on the loan type, purchase price, and available assistance programs. Many buyers are surprised to learn that they may need far less upfront than expected. Down payment assistance programs, seller contributions, and specialized loan products can significantly reduce out-of-pocket costs. A consultation with a Bespoke real estate professional and lender can help determine the best options for your situation." },
  { q: "Is now a good time to buy a home?", a: "The best time to buy a home depends on your personal goals, finances, and lifestyle, not just interest rates. While the market may fluctuate, buyers often benefit from less competition, negotiation opportunities, and long-term equity growth. A knowledgeable Realtor can help you evaluate whether buying now aligns with your financial and life plans." },
  { q: "What credit score do I need to buy a home?", a: "Credit score requirements vary by loan program. Some buyers qualify with lower credit scores than they expect, especially through FHA or specialized programs. If your credit is not where it needs to be, a lender can provide guidance on improving it while a Realtor helps you prepare for future homeownership." },
  { q: "What is the home buying process step by step?", a: "The home buying process typically includes getting pre-approved, selecting a home, making an offer, completing inspections, finalizing financing, and closing. Working with an experienced real estate agent ensures each step is explained clearly, timelines are managed, and surprises are minimized." },
  { q: "How long does it take to buy a home?", a: "On average, the home buying process takes 30 to 60 days once under contract. However, timelines vary based on financing, inspections, and market conditions. Preparation and professional guidance can help keep the process smooth and efficient." },
  { q: "What are closing costs and who pays them?", a: "Closing costs include lender fees, title services, taxes, and insurance. These costs can often be shared between the buyer and seller, depending on the contract terms. In many cases, sellers may contribute toward closing costs, reducing the buyer's upfront expenses." },
  { q: "Can I buy a home after bankruptcy or foreclosure?", a: "Yes, many buyers can purchase a home after bankruptcy or foreclosure once required waiting periods have passed. Each situation is unique, so it's important to work with a lender and Realtor who understand the guidelines and can help you prepare for success." },
  { q: "What is earnest money?", a: "Earnest money is a good-faith deposit submitted with an offer to show the seller you are serious about purchasing the home. The amount varies and is typically applied toward closing costs or the down payment at closing." },
  { q: "What programs are available for first-time homebuyers?", a: "First-time buyers may qualify for down payment assistance, grants, reduced interest rates, and special loan programs. Availability depends on location, income, and credit qualifications. A local Realtor can connect you with trusted lenders who specialize in these programs." },
  { q: "Can I buy a home with little or no money down?", a: "Yes, some loan programs allow for low or no down payment options. Assistance programs and seller contributions may also help cover costs. The key is understanding which options fit your financial profile." },
  { q: "Should I get pre-approved before looking at homes?", a: "Yes. A pre-approval helps determine your budget, strengthens your offer, and prevents delays. It also allows you to shop with confidence and clarity in a competitive market." },
  { q: "What mistakes should first-time buyers avoid?", a: "Common mistakes include skipping pre-approval, underestimating costs, and making emotional decisions. Working with an experienced real estate agent helps you avoid costly missteps and stay focused on long-term goals." },
  { q: "How much is my home worth?", a: "Your home's value is based on recent sales, market trends, location, and condition. An experienced Realtor provides a comparative market analysis to determine a competitive and realistic price." },
  { q: "How long does it take to sell a home?", a: "Homes can sell in days or weeks depending on pricing, condition, and market conditions. Strategic pricing and professional marketing often lead to faster, more profitable sales." },
  { q: "Do I need to make repairs before selling?", a: "Not always. Some updates can increase value, while others may not provide a return. A Realtor can advise which improvements, if any, make the most sense for your home." },
  { q: "What costs should sellers expect?", a: "Sellers may be responsible for commissions, closing fees, and negotiated repairs. Understanding these costs upfront allows for better planning and fewer surprises." },
  { q: "Do I need a Realtor when buying new construction?", a: "Yes. A Realtor represents your interests, helps negotiate terms, and ensures you understand contracts and incentives. The builder's agent represents the builder, not the buyer." },
  { q: "Can I negotiate on new construction homes?", a: "In many cases, yes. Builders may offer incentives such as closing cost assistance, upgrades, or rate buy-downs, especially when working with a Realtor." },
  { q: "How long does it take to build a new home?", a: "Build times vary depending on the builder, weather, and customization level. Most new construction homes take several months from contract to completion." },
  { q: "Why should I work with Bespoke Realty?", a: "Bespoke Realty provides a people-first, tailored real estate experience. Every client receives education, strategy, and personalized service designed to align with their goals, not just close a transaction. As a Black-owned, woman-led brokerage, we're committed to building lasting relationships in our community." },
  { q: "What areas do you serve?", a: "Bespoke Realty serves clients throughout Mississippi, Tennessee, and Arkansas, including Memphis, Southaven, Olive Branch, Hernando, Collierville, Germantown, Byhalia, Nesbit, and surrounding areas. We specialize in residential, luxury, and new construction homes." },
  { q: "What does a luxury real estate experience mean?", a: "Luxury is not a price point, it's the experience. It means clear communication, attention to detail, strategic guidance, and exceptional service from start to finish. Every Bespoke client receives a luxury experience, regardless of their home's price." },
];

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": FAQ_SCHEMA_ITEMS.map(({ q, a }) => ({
    "@type": "Question",
    "name": q,
    "acceptedAnswer": { "@type": "Answer", "text": a },
  })),
};

const FAQ = () => {
  // Inject FAQPage JSON-LD schema into <head> for Google Rich Results
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'faq-page-schema';
    script.textContent = JSON.stringify(FAQ_SCHEMA);
    document.head.appendChild(script);
    return () => {
      const existing = document.getElementById('faq-page-schema');
      if (existing) existing.remove();
    };
  }, []);

  return (
    <div className="min-h-screen" data-testid="faq-page">
      <SEO 
        title="Frequently Asked Questions | Home Buying & Selling FAQs"
        description="Get answers to common real estate questions about buying a home in Mississippi or Tennessee, first-time buyer programs, selling your home, new construction, and working with Bespoke Realty."
        keywords="home buying FAQ, first time home buyer questions, how to buy a home Mississippi, how to sell a home Tennessee, new construction FAQ, real estate questions Memphis"
      />

      {/* Hero */}
      <section className="relative h-[40vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-black/60 z-10"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1920&q=80)',
          }}
        ></div>
        <div className="relative z-20 text-center px-4 max-w-4xl mx-auto">
          <h1 className="text-5xl font-bold mb-4 text-white" data-testid="faq-heading">Frequently Asked Questions</h1>
          <p className="text-xl text-white/90">Everything you need to know about buying and selling real estate in Mississippi, Tennessee, and Arkansas</p>
        </div>
      </section>

      {/* Quick Navigation */}
      <section className="py-8 bg-[#BCA480]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-3">
            <a href="#general" className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-full text-sm font-medium transition-colors">General Buying</a>
            <a href="#first-time" className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-full text-sm font-medium transition-colors">First-Time Buyers</a>
            <a href="#sellers" className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-full text-sm font-medium transition-colors">Sellers</a>
            <a href="#new-construction" className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-full text-sm font-medium transition-colors">New Construction</a>
            <a href="#bespoke" className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-full text-sm font-medium transition-colors">Working With Us</a>
          </div>
        </div>
      </section>

      {/* General Home Buying FAQs */}
      <section id="general" className="py-16 bg-white scroll-mt-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center mb-8">
            <Home className="h-8 w-8 text-[#BCA480] mr-3" />
            <h2 className="text-3xl font-bold text-[#434345]">Home Buying Questions</h2>
          </div>
          
          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="gen-1" className="border-2 border-[#BCA480]/30 rounded-lg px-6 bg-white shadow-sm">
              <AccordionTrigger className="text-lg font-semibold text-[#BCA480] hover:text-[#a89470] hover:no-underline text-left">
                How much do I need to buy a home in Mississippi, Tennessee, or Arkansas?
              </AccordionTrigger>
              <AccordionContent className="text-gray-800 pt-2 leading-relaxed">
                The amount needed to buy a home varies based on the loan type, purchase price, and available assistance programs. Many buyers are surprised to learn that they may need far less upfront than expected. Down payment assistance programs, seller contributions, and specialized loan products can significantly reduce out-of-pocket costs. A consultation with a Bespoke real estate professional and lender can help determine the best options for your situation.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="gen-2" className="border-2 border-[#BCA480]/30 rounded-lg px-6 bg-white shadow-sm">
              <AccordionTrigger className="text-lg font-semibold text-[#BCA480] hover:text-[#a89470] hover:no-underline text-left">
                Is now a good time to buy a home?
              </AccordionTrigger>
              <AccordionContent className="text-gray-800 pt-2 leading-relaxed">
                The best time to buy a home depends on your personal goals, finances, and lifestyle—not just interest rates. While the market may fluctuate, buyers often benefit from less competition, negotiation opportunities, and long-term equity growth. A knowledgeable Realtor can help you evaluate whether buying now aligns with your financial and life plans.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="gen-3" className="border-2 border-[#BCA480]/30 rounded-lg px-6 bg-white shadow-sm">
              <AccordionTrigger className="text-lg font-semibold text-[#BCA480] hover:text-[#a89470] hover:no-underline text-left">
                What credit score do I need to buy a home?
              </AccordionTrigger>
              <AccordionContent className="text-gray-800 pt-2 leading-relaxed">
                Credit score requirements vary by loan program. Some buyers qualify with lower credit scores than they expect, especially through FHA or specialized programs. If your credit is not where it needs to be, a lender can provide guidance on improving it while a Realtor helps you prepare for future homeownership.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="gen-4" className="border-2 border-[#BCA480]/30 rounded-lg px-6 bg-white shadow-sm">
              <AccordionTrigger className="text-lg font-semibold text-[#BCA480] hover:text-[#a89470] hover:no-underline text-left">
                What is the home buying process step by step?
              </AccordionTrigger>
              <AccordionContent className="text-gray-800 pt-2 leading-relaxed">
                The home buying process typically includes getting pre-approved, selecting a home, making an offer, completing inspections, finalizing financing, and closing. Working with an experienced real estate agent ensures each step is explained clearly, timelines are managed, and surprises are minimized.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="gen-5" className="border-2 border-[#BCA480]/30 rounded-lg px-6 bg-white shadow-sm">
              <AccordionTrigger className="text-lg font-semibold text-[#BCA480] hover:text-[#a89470] hover:no-underline text-left">
                How long does it take to buy a home?
              </AccordionTrigger>
              <AccordionContent className="text-gray-800 pt-2 leading-relaxed">
                On average, the home buying process takes 30 to 60 days once under contract. However, timelines vary based on financing, inspections, and market conditions. Preparation and professional guidance can help keep the process smooth and efficient.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="gen-6" className="border-2 border-[#BCA480]/30 rounded-lg px-6 bg-white shadow-sm">
              <AccordionTrigger className="text-lg font-semibold text-[#BCA480] hover:text-[#a89470] hover:no-underline text-left">
                What are closing costs and who pays them?
              </AccordionTrigger>
              <AccordionContent className="text-gray-800 pt-2 leading-relaxed">
                Closing costs include lender fees, title services, taxes, and insurance. These costs can often be shared between the buyer and seller, depending on the contract terms. In many cases, sellers may contribute toward closing costs, reducing the buyer's upfront expenses.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="gen-7" className="border-2 border-[#BCA480]/30 rounded-lg px-6 bg-white shadow-sm">
              <AccordionTrigger className="text-lg font-semibold text-[#BCA480] hover:text-[#a89470] hover:no-underline text-left">
                Can I buy a home after bankruptcy or foreclosure?
              </AccordionTrigger>
              <AccordionContent className="text-gray-800 pt-2 leading-relaxed">
                Yes, many buyers can purchase a home after bankruptcy or foreclosure once required waiting periods have passed. Each situation is unique, so it's important to work with a lender and Realtor who understand the guidelines and can help you prepare for success.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="gen-8" className="border-2 border-[#BCA480]/30 rounded-lg px-6 bg-white shadow-sm">
              <AccordionTrigger className="text-lg font-semibold text-[#BCA480] hover:text-[#a89470] hover:no-underline text-left">
                What is earnest money?
              </AccordionTrigger>
              <AccordionContent className="text-gray-800 pt-2 leading-relaxed">
                Earnest money is a good-faith deposit submitted with an offer to show the seller you are serious about purchasing the home. The amount varies and is typically applied toward closing costs or the down payment at closing.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* First-Time Buyer FAQs */}
      <section id="first-time" className="py-16 bg-gray-50 scroll-mt-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center mb-8">
            <Sparkles className="h-8 w-8 text-[#BCA480] mr-3" />
            <h2 className="text-3xl font-bold text-[#434345]">First-Time Buyer Questions</h2>
          </div>
          
          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="ftb-1" className="border rounded-lg px-6 bg-white">
              <AccordionTrigger className="text-lg font-semibold text-[#BCA480] hover:text-[#a89470] hover:no-underline text-left">
                What programs are available for first-time homebuyers?
              </AccordionTrigger>
              <AccordionContent className="text-gray-800 pt-2 leading-relaxed">
                First-time buyers may qualify for down payment assistance, grants, reduced interest rates, and special loan programs. Availability depends on location, income, and credit qualifications. A local Realtor can connect you with trusted lenders who specialize in these programs.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="ftb-2" className="border rounded-lg px-6 bg-white">
              <AccordionTrigger className="text-lg font-semibold text-[#BCA480] hover:text-[#a89470] hover:no-underline text-left">
                Can I buy a home with little or no money down?
              </AccordionTrigger>
              <AccordionContent className="text-gray-800 pt-2 leading-relaxed">
                Yes, some loan programs allow for low or no down payment options. Assistance programs and seller contributions may also help cover costs. The key is understanding which options fit your financial profile.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="ftb-3" className="border rounded-lg px-6 bg-white">
              <AccordionTrigger className="text-lg font-semibold text-[#BCA480] hover:text-[#a89470] hover:no-underline text-left">
                Should I get pre-approved before looking at homes?
              </AccordionTrigger>
              <AccordionContent className="text-gray-800 pt-2 leading-relaxed">
                Yes. A pre-approval helps determine your budget, strengthens your offer, and prevents delays. It also allows you to shop with confidence and clarity in a competitive market.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="ftb-4" className="border rounded-lg px-6 bg-white">
              <AccordionTrigger className="text-lg font-semibold text-[#BCA480] hover:text-[#a89470] hover:no-underline text-left">
                What mistakes should first-time buyers avoid?
              </AccordionTrigger>
              <AccordionContent className="text-gray-800 pt-2 leading-relaxed">
                Common mistakes include skipping pre-approval, underestimating costs, and making emotional decisions. Working with an experienced real estate agent helps you avoid costly missteps and stay focused on long-term goals.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Seller FAQs */}
      <section id="sellers" className="py-16 bg-white scroll-mt-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center mb-8">
            <DollarSign className="h-8 w-8 text-[#BCA480] mr-3" />
            <h2 className="text-3xl font-bold text-[#434345]">Seller Questions</h2>
          </div>
          
          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="sell-1" className="border-2 border-[#BCA480]/30 rounded-lg px-6 bg-white shadow-sm">
              <AccordionTrigger className="text-lg font-semibold text-[#BCA480] hover:text-[#a89470] hover:no-underline text-left">
                How much is my home worth?
              </AccordionTrigger>
              <AccordionContent className="text-gray-800 pt-2 leading-relaxed">
                Your home's value is based on recent sales, market trends, location, and condition. An experienced Realtor provides a comparative market analysis to determine a competitive and realistic price.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="sell-2" className="border-2 border-[#BCA480]/30 rounded-lg px-6 bg-white shadow-sm">
              <AccordionTrigger className="text-lg font-semibold text-[#BCA480] hover:text-[#a89470] hover:no-underline text-left">
                How long does it take to sell a home?
              </AccordionTrigger>
              <AccordionContent className="text-gray-800 pt-2 leading-relaxed">
                Homes can sell in days or weeks depending on pricing, condition, and market conditions. Strategic pricing and professional marketing often lead to faster, more profitable sales.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="sell-3" className="border-2 border-[#BCA480]/30 rounded-lg px-6 bg-white shadow-sm">
              <AccordionTrigger className="text-lg font-semibold text-[#BCA480] hover:text-[#a89470] hover:no-underline text-left">
                Do I need to make repairs before selling?
              </AccordionTrigger>
              <AccordionContent className="text-gray-800 pt-2 leading-relaxed">
                Not always. Some updates can increase value, while others may not provide a return. A Realtor can advise which improvements, if any, make the most sense for your home.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="sell-4" className="border-2 border-[#BCA480]/30 rounded-lg px-6 bg-white shadow-sm">
              <AccordionTrigger className="text-lg font-semibold text-[#BCA480] hover:text-[#a89470] hover:no-underline text-left">
                What costs should sellers expect?
              </AccordionTrigger>
              <AccordionContent className="text-gray-800 pt-2 leading-relaxed">
                Sellers may be responsible for commissions, closing fees, and negotiated repairs. Understanding these costs upfront allows for better planning and fewer surprises.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* New Construction FAQs */}
      <section id="new-construction" className="py-16 bg-gray-50 scroll-mt-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center mb-8">
            <Building2 className="h-8 w-8 text-[#BCA480] mr-3" />
            <h2 className="text-3xl font-bold text-[#434345]">New Construction Questions</h2>
          </div>
          
          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="nc-1" className="border rounded-lg px-6 bg-white">
              <AccordionTrigger className="text-lg font-semibold text-[#BCA480] hover:text-[#a89470] hover:no-underline text-left">
                Do I need a Realtor when buying new construction?
              </AccordionTrigger>
              <AccordionContent className="text-gray-800 pt-2 leading-relaxed">
                Yes. A Realtor represents your interests, helps negotiate terms, and ensures you understand contracts and incentives. The builder's agent represents the builder, not the buyer. <Link to="/blog/53116e51-cdad-4686-afa3-915dfd74024a" className="text-[#BCA480] hover:underline font-medium">Learn more in our blog post →</Link>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="nc-2" className="border rounded-lg px-6 bg-white">
              <AccordionTrigger className="text-lg font-semibold text-[#BCA480] hover:text-[#a89470] hover:no-underline text-left">
                Can I negotiate on new construction homes?
              </AccordionTrigger>
              <AccordionContent className="text-gray-800 pt-2 leading-relaxed">
                In many cases, yes. Builders may offer incentives such as closing cost assistance, upgrades, or rate buy-downs, especially when working with a Realtor.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="nc-3" className="border rounded-lg px-6 bg-white">
              <AccordionTrigger className="text-lg font-semibold text-[#BCA480] hover:text-[#a89470] hover:no-underline text-left">
                How long does it take to build a new home?
              </AccordionTrigger>
              <AccordionContent className="text-gray-800 pt-2 leading-relaxed">
                Build times vary depending on the builder, weather, and customization level. Most new construction homes take several months from contract to completion.
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          <div className="mt-8 text-center">
            <Button asChild className="bg-[#BCA480] hover:bg-[#a89470]">
              <Link to="/new-construction">Explore New Construction Communities →</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Working with Bespoke FAQs */}
      <section id="bespoke" className="py-16 bg-white scroll-mt-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center mb-8">
            <Users className="h-8 w-8 text-[#BCA480] mr-3" />
            <h2 className="text-3xl font-bold text-[#434345]">Working with Bespoke Realty</h2>
          </div>
          
          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="br-1" className="border-2 border-[#BCA480]/30 rounded-lg px-6 bg-white shadow-sm">
              <AccordionTrigger className="text-lg font-semibold text-[#BCA480] hover:text-[#a89470] hover:no-underline text-left">
                Why should I work with Bespoke Realty?
              </AccordionTrigger>
              <AccordionContent className="text-gray-800 pt-2 leading-relaxed">
                Bespoke Realty provides a people-first, tailored real estate experience. Every client receives education, strategy, and personalized service designed to align with their goals—not just close a transaction. As a Black-owned, woman-led brokerage, we're committed to building lasting relationships in our community.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="br-2" className="border-2 border-[#BCA480]/30 rounded-lg px-6 bg-white shadow-sm">
              <AccordionTrigger className="text-lg font-semibold text-[#BCA480] hover:text-[#a89470] hover:no-underline text-left">
                What areas do you serve?
              </AccordionTrigger>
              <AccordionContent className="text-gray-800 pt-2 leading-relaxed">
                Bespoke Realty serves clients throughout Mississippi, Tennessee, and Arkansas, including Memphis, Southaven, Olive Branch, Hernando, Collierville, Germantown, Byhalia, Nesbit, and surrounding areas. We specialize in residential, luxury, and new construction homes.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="br-3" className="border-2 border-[#BCA480]/30 rounded-lg px-6 bg-white shadow-sm">
              <AccordionTrigger className="text-lg font-semibold text-[#BCA480] hover:text-[#a89470] hover:no-underline text-left">
                What does a luxury real estate experience mean?
              </AccordionTrigger>
              <AccordionContent className="text-gray-800 pt-2 leading-relaxed">
                Luxury is not a price point—it's the experience. It means clear communication, attention to detail, strategic guidance, and exceptional service from start to finish. Every Bespoke client receives a luxury experience, regardless of their home's price.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-[#434345] to-[#5a5a5c]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">Still Have Questions?</h2>
          <p className="text-white/90 mb-8 max-w-2xl mx-auto">
            We're here to help. Schedule a free consultation with one of our experienced agents 
            and get personalized answers to all your real estate questions.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button asChild size="lg" className="bg-[#BCA480] hover:bg-[#a89470]">
              <Link to="/contact">Contact Us</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-[#434345]">
              <Link to="/buy">Start Your Home Search</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;
